import { useMemo, useState } from 'react'
import LoanEventsTable from './LoanEventsTable'
import type {
	LoanEventFilters,
	LoanEventSorting,
	LoanViewFilters,
} from '../types'
import EventFilters from './EventFilters'
import useLoanEvents from '../useLoanEvents'
import LoadingState from 'ui/components/LoadingState'
import FloatingActionBtn from 'ui/components/FloatingActionBtn'
import { useIntl } from 'react-intl'
import LoanEventCreateDialog from './LoanEventCreateDialog'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import { useBuildings } from 'ui/CompanyBuildings/hooks'
import { createApartmentOptions } from '@viilu-web/shared/util/apartmentOptions'
import type {
	Loan,
	LoanEvent,
	LoanShareholder,
} from '@viilu-web/shared/types/loans'
import type { LoanEventType } from '@viilu-web/shared'

type Props = {
	loan: Loan
	shareholders: LoanShareholder[]
	refreshLoan: () => void
	filters: LoanViewFilters
	setFilters: React.Dispatch<React.SetStateAction<LoanEventFilters>>
}

function LoanEventView({
	loan,
	shareholders,
	refreshLoan,
	filters,
	setFilters,
}: Props) {
	const intl = useIntl()
	const [createEventDialogVisible, setCreateEventDialogVisible] =
		useState(false)

	const [previewEvent, setPreviewEvent] = useState<LoanEvent | undefined>(
		undefined,
	)
	const [type, setType] = useState<LoanEventType | undefined>(undefined)

	const [sort, setSort] = useState<LoanEventSorting>({
		column: 'eventDate',
		order: 'asc',
	})

	const combinedFilters = useMemo(
		() => ({ companyUUID: filters.companyUUID, type }),
		[filters, type],
	)

	const { companyApartments } = useCompanyApartments(
		combinedFilters.companyUUID,
	)
	const { buildings } = useBuildings(combinedFilters.companyUUID)
	const { events, loading, refresh } = useLoanEvents(loan.uuid, combinedFilters)

	const loanEventsInRange = useMemo(() => {
		if (!events || events.length === 0 || loading) return []
		return events.filter((event) => {
			const eventDate = new Date(event.eventDate)
			const startDate = filters.startDate
				? new Date(filters.startDate)
				: new Date(loan.dateDrawn || '')
			const endDate = filters.endDate ? new Date(filters.endDate) : new Date()
			return eventDate >= startDate && eventDate <= endDate
		})
	}, [events, filters.startDate, filters.endDate, loading, loan.dateDrawn])

	const apartmentOptions = createApartmentOptions(companyApartments, buildings)

	const changeSort = (column: keyof LoanEvent) => {
		if (sort.column === column) {
			setSort((prevData) => ({
				...prevData,
				order: sort.order === 'asc' ? 'desc' : 'asc',
			}))
		} else {
			setSort({
				column,
				order: 'asc',
			})
		}
	}

	return (
		<>
			<EventFilters
				filters={filters}
				setFilters={setFilters}
				type={type}
				setType={setType}
			/>
			{loading && (
				<span style={{ margin: '4rem' }}>
					<LoadingState />
				</span>
			)}
			{!loading && (
				<LoanEventsTable
					events={events || []}
					apartmentOptions={apartmentOptions}
					onEventClick={(event) => setPreviewEvent(event)}
					sort={sort}
					changeSort={changeSort}
				/>
			)}
			{createEventDialogVisible && (
				<LoanEventCreateDialog
					loan={loan}
					companyUUID={combinedFilters.companyUUID}
					shareholders={shareholders}
					allEvents={events || []}
					allEventsInRange={loanEventsInRange}
					onHide={() => setCreateEventDialogVisible(false)}
					refreshEvents={refresh}
					refreshLoan={refreshLoan}
				/>
			)}
			{previewEvent && (
				<LoanEventCreateDialog
					loan={loan}
					companyUUID={combinedFilters.companyUUID}
					shareholders={shareholders}
					event={previewEvent}
					allEvents={events || []}
					allEventsInRange={loanEventsInRange}
					onHide={() => setPreviewEvent(undefined)}
					refreshEvents={refresh}
					refreshLoan={refreshLoan}
				/>
			)}
			<FloatingActionBtn
				iconName={'add'}
				text={intl.formatMessage({
					defaultMessage: 'Luo uusi tapahtuma',
					description:
						'Floating action button label for creating new loan event',
				})}
				action={() => setCreateEventDialogVisible(true)}
			/>
		</>
	)
}

export default LoanEventView
